/**
 * The names a questionnaire already uses, read from its plain text. The
 * template wizard offers them (lists to point at, exceptions already there)
 * and warns before a new question name clashes; the server's tray merge
 * remains the authoritative check.
 */
export interface QuestionnaireNames {
  /** `Q <label>:` */
  questions: string[]
  /** `HIDDEN <label>` */
  hidden: string[]
  /** `DATA <label>` */
  data: string[]
  /** `LIST <name>` (not `LIST EXCEPTIONS`, which is an EXCEPTIONS block) */
  lists: string[]
  /** `RANGE LIST <name>:` */
  rangeLists: string[]
  /** The items of every EXCEPTIONS block */
  exceptions: string[]
}

const QUESTION = /^\s*Q\s+([^:]+?)\s*:/
const HIDDEN = /^\s*HIDDEN\s+([^:]+?)\s*(?::|$)/
const DATA = /^\s*DATA\s+([^:]+?)\s*(?::|$)/
const RANGE_LIST = /^\s*RANGE\s+LIST\s+([^:]+?)\s*:/
const LIST = /^\s*LIST\s+([^:]+?)\s*$/
const EXCEPTIONS = /^\s*(?:LIST\s+)?EXCEPTIONS\s*$/
/** A line that starts with a keyword, so it also ends an EXCEPTIONS block. */
const KEYWORD =
  /^\s*(?:Q|HIDDEN|DATA|LIST|RANGE\s+LIST|EXCEPTIONS|SURVEY|IF|ELSE|PER|RECODE|NOTE|FOR\s+EACH|BEGIN|END|TEXT|SECTION|SELECT|FILL|RANDOMIZE|EXTEND|LOCATION|SKIP|TERMINATE)\b/

const pushOnce = (into: string[], name: string): void => {
  if (name !== "" && !into.includes(name)) into.push(name)
}

/**
 * Reads the question, hidden, data, list and range list names and the
 * exceptions from a questionnaire text. Each name appears once, in the
 * order it first appears.
 *
 * @param text - The plain questionnaire text
 */
export const readQuestionnaireNames = (text: string): QuestionnaireNames => {
  const names: QuestionnaireNames = {
    questions: [],
    hidden: [],
    data: [],
    lists: [],
    rangeLists: [],
    exceptions: [],
  }
  let inExceptions = false

  for (const line of text.split(/\r?\n/)) {
    if (inExceptions) {
      if (line.trim() === "") {
        inExceptions = false
        continue
      }
      if (line.trim().startsWith("#")) continue
      if (!KEYWORD.test(line)) {
        pushOnce(names.exceptions, line.trim())
        continue
      }
      inExceptions = false
    }

    let match: RegExpMatchArray | null
    if (EXCEPTIONS.test(line)) {
      inExceptions = true
    } else if ((match = line.match(QUESTION))) {
      pushOnce(names.questions, match[1])
    } else if ((match = line.match(HIDDEN))) {
      pushOnce(names.hidden, match[1])
    } else if ((match = line.match(DATA))) {
      pushOnce(names.data, match[1])
    } else if ((match = line.match(RANGE_LIST))) {
      pushOnce(names.rangeLists, match[1])
    } else if ((match = line.match(LIST))) {
      pushOnce(names.lists, match[1])
    }
  }

  return names
}
